import * as React from "react"
import {
  Box,
  Typography,
  Badge,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  TextField,
  Stack,
  Chip,
  Divider,
  Grid,
  Pagination,
} from "@mui/material"
import ExpandMoreIcon from "@mui/icons-material/ExpandMore"

type Meeting = {
  id: string
  summary?: string | null
  organizer?: { email?: string | null, display_name?: string | null }
  attendees?: Array<{ email?: string | null, display_name?: string | null }>
  start?: { date_time?: string | null, date?: string | null }
  end?: { date_time?: string | null, date?: string | null }
}

function eventToDateKey(m: Meeting): string | null {
  const raw = m?.start?.date_time || m?.start?.date
  if (!raw) return null

  const m1 = raw.match(/^(\d{4})\/(\d{2})\/(\d{2})/)
  if (m1) return `${m1[1]}-${m1[2]}-${m1[3]}`

  const m2 = raw.match(/^(\d{4})-(\d{2})-(\d{2})/)
  if (m2) return `${m2[1]}-${m2[2]}-${m2[3]}`

  const d = new Date(raw)
  if (Number.isNaN(d.getTime())) return null
  const yyyy = d.getFullYear()
  const mm = String(d.getMonth() + 1).padStart(2, "0")
  const dd = String(d.getDate()).padStart(2, "0")
  return `${yyyy}-${mm}-${dd}`
}

function getStartSortable(m: Meeting): string {
  return m?.start?.date_time || m?.start?.date || ""
}

function norm(s: unknown) {
  return String(s ?? "").toLowerCase()
}

function meetingMatches(m: Meeting, q: string) {
  if (!q) return true
  const hay = [
    m.summary,
    m.organizer?.display_name,
    m.organizer?.email,
    ...(m.attendees ?? []).flatMap((a) => [a.display_name, a.email]),
    m.start?.date_time,
    m.start?.date,
  ]
    .map(norm)
    .join(" ")
  return hay.includes(q)
}

type DayGroup = { dateKey: string, meetings: Meeting[] }

function groupByDay(meetings: Meeting[]): DayGroup[] {
  const map = new Map<string, Meeting[]>()
  for (const m of meetings ?? []) {
    const key = eventToDateKey(m)
    if (!key) continue
    if (!map.has(key)) map.set(key, [])
    map.get(key)!.push(m)
  }

  const keys = Array.from(map.keys()).sort((a, b) => (a > b ? -1 : 1))

  return keys.map((k) => ({
    dateKey: k,
    meetings: map
      .get(k)!
      .slice()
      .sort((a, b) => (getStartSortable(a) > getStartSortable(b) ? 1 : -1)),
  }))
}

export function MeetingsTimeline({ meetings }: { meetings: Meeting[] }) {
  const [q, setQ] = React.useState("")

  const filtered = React.useMemo(() => {
    const qq = norm(q).trim()
    if (!qq) return meetings ?? []
    return (meetings ?? []).filter((m) => meetingMatches(m, qq))
  }, [meetings, q])

  const groups = React.useMemo(() => groupByDay(filtered), [filtered])

  const PAGE_SIZE = 6

  const [page, setPage] = React.useState(1)

  const pageCount = Math.max(1, Math.ceil(groups.length / PAGE_SIZE))

  React.useEffect(() => {
    setPage((p) => Math.min(p, pageCount))
  }, [pageCount])

  const pageGroups = React.useMemo(() => {
    const start = (page - 1) * PAGE_SIZE
    return groups.slice(start, start + PAGE_SIZE)
  }, [groups, page])

  return (
    <Box>
      <TextField
        fullWidth
        size="small"
        label="Search (keyword)"
        value={q}
        onChange={(e) => setQ(e.target.value)}
        sx={{ mb: 2 }}
      />

      {groups.length === 0 && (
        <Typography color="text.secondary">No meetings found.</Typography>
      )}

      <Grid container spacing={2}>
        {pageGroups.map((g) => (
          <Grid item xs={6} md={6} key={g.dateKey}>
            <Accordion>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Grid container alignItems="center" wrap="nowrap" spacing={1}>
                  <Grid item>
                    <Chip size="small" label={g.dateKey} />
                  </Grid>

                  <Grid item>
                    <Badge
                      color="primary"
                      badgeContent={g.meetings.length}
                      anchorOrigin={{ vertical: "top", horizontal: "right" }}
                      sx={{
                        "& .MuiBadge-badge": {
                          transform: "translate(40%, -40%)",
                        },
                      }}
                    >
                      <Box sx={{ width: 18, height: 18 }} />
                    </Badge>
                  </Grid>
                </Grid>
              </AccordionSummary>

              <AccordionDetails>
                <Stack spacing={1}>
                  {g.meetings.map((m) => {
                    const organizer =
                      m.organizer?.display_name || m.organizer?.email || "—"

                    const start = m.start?.date_time ?? m.start?.date ?? "—"
                    const end = m.end?.date_time ?? m.end?.date ?? "—"

                    return (
                      <Box
                        key={m.id}
                        sx={{
                          p: 2,
                          border: "1px solid",
                          borderColor: "divider",
                          borderRadius: 2,
                          bgcolor: "background.paper",
                        }}
                      >
                        <Stack spacing={1}>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "baseline",
                              justifyContent: "space-between",
                              gap: 2,
                            }}
                          >
                            <Typography fontWeight={800} sx={{ lineHeight: 1.2 }}>
                              {m.summary ?? "(no title)"}
                            </Typography>

                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{ whiteSpace: "nowrap" }}
                            >
                              {start} → {end}
                            </Typography>
                          </Box>

                          <Divider />

                          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                            <Typography variant="body2" color="text.secondary">
                              Organizer:
                            </Typography>
                            <Typography
                              variant="body2"
                              fontWeight={700}
                              sx={{ wordBreak: "break-word" }}
                            >
                              {organizer}
                            </Typography>
                          </Box>
                        </Stack>
                      </Box>
                    )
                  })}
                </Stack>
              </AccordionDetails>
            </Accordion>
          </Grid>
        ))}
      </Grid>

      <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
        <Pagination
          count={pageCount}
          page={page}
          onChange={(_, v) => setPage(v)}
          shape="rounded"
        />
      </Box>
    </Box>
  )
}
